import { splitDayEntryByDay, type SplittableEntry, type WorkedDayType } from './worked-hours-split'
import {
  resolveToeslagen,
  type ToeslagKind,
  type ToeslagDienstType,
  type ToeslagPloegSoort,
  type ToeslagRegelInput,
  type ToeslagValueType,
} from './toeslag-resolver'
import type { PublicHolidayInfo } from './leave-calculations'

/**
 * Component-aggregatie voor toeslagen (spec 23 fase 2).
 *
 * Combineert de twee bestaande pure helpers:
 *  - `splitDayEntryByDay` → autoritatieve netto-uren per kalenderdag/ISO-week +
 *    `dagType` (WERKDAG/ZATERDAG/ZONDAG/FEESTDAG), incl. over-middernacht-split.
 *  - `resolveToeslagen` → de matchende, stapelbare toeslag-regels per
 *    (dagType × ploegSoort), met cascade klant-specifiek → systeem-default.
 *
 * Resultaat: losse toeslag-**componenten** (één per uur-bucket × matchende regel)
 * en een aggregaat per regel (optioneel per ISO-week). Bewust **geen geld** hier:
 * deze helper is puur + zonder Decimal testbaar. De caller (payroll = VERLONING,
 * invoice = FACTURATIE) vermenigvuldigt `uren × tarief × value` met Decimal-
 * precisie en rondt pas op het laatste moment af.
 */

export interface ToeslagComponent {
  /** ISO-week `wk-jaar` van het deel (voor factuur-weeklabel / periode-allocatie). */
  isoWeek: string
  /** Kalenderdag van het deel (`yyyy-MM-dd`). */
  datum: string
  /** dagType van het uur-bucket (zo 22–24 = ZONDAG, ma 00–06 = WERKDAG). */
  dagType: WorkedDayType
  /** ploegSoort van de werkbon (bron van de NACHT/VROEG/LAAT-scope). */
  ploegSoort: ToeslagPloegSoort | null
  /** Netto uren in dit deel. */
  uren: number
  regelId: string
  label: string | null
  valueType: ToeslagValueType
  /** Fractie (1.0 = 100%, 0.26 = 26%) bij PERCENTAGE; €/uur bij BEDRAG. */
  value: number
}

export interface ComputeToeslagInput {
  entry: SplittableEntry
  /** ploegSoort van de werkbon — bepaalt mede welke ploeg-regels matchen. */
  ploegSoort: ToeslagPloegSoort | null
  /** Ploegensysteem van de werkbon — bron van de vaste ploegentoeslag (Variant C). */
  dienstType?: ToeslagDienstType | null
  /** Klant van de werkbon — voor de klant→systeem-default cascade. */
  clientId: string | null
  /** Monteur — voor de medewerker-afwijking (3e cascade-laag). */
  employeeId?: string | null
  kind: ToeslagKind
  regels: ToeslagRegelInput[]
  /** Feestdagen voor dagType=FEESTDAG-afleiding (anders kalenderdag-gebaseerd). */
  feestdagen?: PublicHolidayInfo[]
  /** Portal interne bon (batch-juni C): true = nooit toeslag (alle componenten 0). Bron-van-waarheid. */
  geenToeslag?: boolean
  /** Portal interne bon (batch-juni C): fallback-signaal voor oudere interne bonnen zonder `geenToeslag`. */
  isIntern?: boolean
}

/**
 * Eén dayEntry → toeslag-componenten per (kalenderdag/ISO-week × matchende regel).
 * De som van de uren over de buckets is exact gelijk aan de opgeslagen `hours`
 * (erfenis van `splitDayEntryByDay`), dus toeslag-uren winnen/verliezen nooit.
 */
export function computeToeslagComponentenForEntry(input: ComputeToeslagInput): ToeslagComponent[] {
  // Harde guard (batch-juni C / spec Deel 4): interne bon = nooit toeslag.
  // Geen ploeg-, zaterdag-, zondag-, feestdag- of overuren-toeslag. De engine
  // beslist op één plek: `geenToeslag` (bron-van-waarheid) of `isIntern`
  // (fallback voor oudere bonnen). Callers geven de rauwe vlaggen door.
  if (input.geenToeslag || input.isIntern) return []
  const buckets = splitDayEntryByDay(input.entry, { feestdagen: input.feestdagen })
  const out: ToeslagComponent[] = []
  for (const b of buckets) {
    if (b.uren <= 0) continue
    // Referentiedatum = BEGIN van de werkelijke kalenderdag (UTC). validFrom/
    // validUntil worden door de config-API op middernacht (T00:00:00Z) opgeslagen;
    // met een start-van-de-dag-referentie zijn beide grenzen INCLUSIEF hun eigen
    // dag (een regel "geldig t/m 20 mei" geldt óók op 20 mei). Per dag-deel zodat
    // zo 22–24 (week N) en ma 00–06 (week N+1) elk in hun eigen periode vallen.
    const referenceDate = new Date(`${b.datum}T00:00:00.000Z`)
    const matched = resolveToeslagen(
      { dagType: b.dayType, ploegSoort: input.ploegSoort, dienstType: input.dienstType ?? null },
      input.kind,
      input.clientId,
      input.regels,
      referenceDate,
      input.employeeId ?? null,
    )
    for (const r of matched) {
      out.push({
        isoWeek: b.isoWeek,
        datum: b.datum,
        dagType: b.dayType,
        ploegSoort: input.ploegSoort,
        uren: b.uren,
        regelId: r.id,
        label: r.label ?? null,
        valueType: r.valueType,
        value: r.value,
      })
    }
  }
  return out
}

export interface AggregatedToeslag {
  /** Alleen gevuld bij `byWeek` (factuur-weeklabel); anders weggelaten. */
  isoWeek?: string
  regelId: string
  label: string | null
  valueType: ToeslagValueType
  value: number
  /** Gesommeerde uren waarop deze toeslag-component van toepassing is. */
  uren: number
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Sommeert componenten tot één regel per toeslag-regel (= één looncomponent /
 * factuurregel). Eén regel (bv. "Nachttoeslag") kan over meerdere dagType-buckets
 * lopen (zondag-deel + maandag-deel van een nachtdienst) — die tellen op tot één
 * component, want de looncomponent is "nachttoeslag", niet "nachttoeslag-op-zondag".
 *
 * `byWeek=true` houdt de weken gescheiden (nodig voor de factuur-weeklabel zodat
 * uren in hun werkelijk gewerkte week gefactureerd worden).
 */
export function aggregateToeslagComponenten(
  components: ToeslagComponent[],
  opts?: { byWeek?: boolean },
): AggregatedToeslag[] {
  const byWeek = opts?.byWeek ?? false
  const map = new Map<string, AggregatedToeslag>()
  for (const c of components) {
    const key = byWeek ? `${c.isoWeek}|${c.regelId}` : c.regelId
    const cur = map.get(key)
    if (cur) {
      cur.uren = round2(cur.uren + c.uren)
    } else {
      map.set(key, {
        ...(byWeek ? { isoWeek: c.isoWeek } : {}),
        regelId: c.regelId,
        label: c.label,
        valueType: c.valueType,
        value: c.value,
        uren: c.uren,
      })
    }
  }
  return [...map.values()]
}
